// supportApi — the in-app support message contract.
//
// MIRROR: functions/contracts/supportApi.js is the CommonJS twin of this file. CRA
// cannot import from outside src/ and Cloud Functions cannot import from src/,
// so any change to a value here MUST land in both files in the same commit.
//
// ── WHY THE SUBJECT IS DERIVED, NOT TYPED ───────────────────────────────────
//
// The member picks a topic; the subject line is built from it. Two reasons.
// Support can triage an inbox by prefix without reading bodies, and a free-text
// subject is one more field to fill before someone who is already frustrated
// gets to say the thing they came to say.
//
// ── VALIDATED ON BOTH SIDES ─────────────────────────────────────────────────
//
// validateSupportInput runs in the browser for the affordance (disabled button,
// live counter) and on the server for the guarantee. The client copy is a
// convenience; deleting the server copy is a vulnerability.

export const SUPPORT_TOPICS = Object.freeze({
  ACCOUNT: "account",
  BILLING: "billing",
  SCAN: "scan",
  GLOWUP: "glowup",
  PRIVACY: "privacy",
  OTHER: "other",
});

export const SUPPORT_TOPIC_KEYS = Object.freeze(Object.values(SUPPORT_TOPICS));

// Subject prefixes. These are join keys for whatever filters the support inbox,
// so they are values, not labels — renaming one splits a mail rule in half.
export const SUPPORT_SUBJECT_PREFIX = Object.freeze({
  account: "Account",
  billing: "Billing",
  scan: "Scan",
  glowup: "Glowup",
  privacy: "Privacy",
  other: "Support",
});

// Long enough to be a real report, short enough that nobody pastes a logfile.
export const SUPPORT_MESSAGE_MIN = 10;
export const SUPPORT_MESSAGE_MAX = 2000;

// Per user, per hour. Generous for anyone with a genuine problem — a member
// following up twice is normal — and low enough that a stuck retry loop cannot
// turn the support inbox into a mailbomb.
export const SUPPORT_MAX_PER_HOUR = 3;

export const SUPPORT_ERRORS = Object.freeze({
  UNAUTHENTICATED: "UNAUTHENTICATED",
  INVALID_INPUT: "INVALID_INPUT",
  RATE_LIMITED: "RATE_LIMITED",
  NO_EMAIL: "NO_EMAIL",
  INTERNAL: "INTERNAL",
});

export function isSupportTopic(value) {
  return typeof value === "string" && SUPPORT_TOPIC_KEYS.includes(value);
}

/**
 * @param {{topic?: string, message?: string}} input
 * @returns {{ok: true}|{ok: false, code: string, reason: string}}
 */
export function validateSupportInput(input) {
  const data = input || {};
  if (!isSupportTopic(data.topic)) {
    return { ok: false, code: SUPPORT_ERRORS.INVALID_INPUT, reason: "topic" };
  }
  const message = typeof data.message === "string" ? data.message.trim() : "";
  if (message.length < SUPPORT_MESSAGE_MIN) {
    return { ok: false, code: SUPPORT_ERRORS.INVALID_INPUT, reason: "message_short" };
  }
  if (message.length > SUPPORT_MESSAGE_MAX) {
    return { ok: false, code: SUPPORT_ERRORS.INVALID_INPUT, reason: "message_long" };
  }
  return { ok: true };
}

/** Subject line for a topic. Falls back to the generic prefix, never throws. */
export function supportSubject(topic) {
  const prefix = SUPPORT_SUBJECT_PREFIX[topic] || SUPPORT_SUBJECT_PREFIX.other;
  return `[${prefix}] Support request`;
}

